import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import { NotificationContext } from '@Context/NotificationContext';
import {
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { FooterActions } from '@Enum/index';
import { useRespondHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { createdUserRequestSelector } from '@Store/selectors/userInfoSelector';
import {
  getCountriesDictSelector,
  getOverseasSponsorsDictSelector,
} from '@Store/selectors/dictionarySelector';
import { useVerifyEmployeeMutation } from '@Store/api/userApiSlice';
import { parseDateTime } from '@Utils/transformDateTime';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { MainForm } from '@Components/index';
import { SponsorTypeCodes } from '@Enum/api/SponsorTypeCodes.enum';
import {
  FormApproveRejectRequest,
  HrmRequestFormResponse,
} from '@Types/api/form';
import ApproveList from '@Components/MainForm/components/ApproveList/ApproveList';
import { useFormListMutation } from '@Store/api/dmsApiSlice';
import { getDocument } from '@Features/utils/document';
import { ApproveListData } from '@Components/MainForm/components/ApproveList/types';
import { ReassignWindow } from '@Components/ReassignWindow';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';

import { initialValuesForm } from '../initialValuesForm';
import { getDocumentFolder } from '../utils/document';

interface OverseasMissionApproveFormProps {
  formData: HrmRequestFormResponse;
  taskId: string;
}

const OverseasMissionCreateForm: React.FC<OverseasMissionApproveFormProps> = ({
  formData,
  taskId,
}) => {
  const {
    taskApprovedNotification,
    taskErrorNotification,
    taskRejectedNotification,
    errorNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();

  const [comments, setCommentValue] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [isReassignOpen, setReassignOpen] = useState(false);

  const countries = useAppSelector(getCountriesDictSelector);
  const requestedUser = useAppSelector(createdUserRequestSelector);
  const sponsors = useAppSelector(getOverseasSponsorsDictSelector);

  const [getFormList] = useFormListMutation();
  const [postApproveRejectForm] = useRespondHrmRequestMutation();
  const requisitionInfo = useRequisitionInfoExistingReq(formData, 'Overseas');
  const [verifyEmployee] = useVerifyEmployeeMutation();

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    onSubmit: (): void => {},
  });

  const handleApproveReject = useCallback(
    (action: FooterActions.APPROVE | FooterActions.REJECT): void => {
      const body: FormApproveRejectRequest = {
        taskId: String(taskId),
        command: action,
        comment: comments,
      };

      if (action === FooterActions.REJECT && !comments) {
        errorNotification('Please leave a comment');
        return;
      }

      postApproveRejectForm(body)
        .unwrap()
        .then(() => {
          if (action === FooterActions.APPROVE) {
            taskApprovedNotification();
          } else {
            taskRejectedNotification();
          }

          handleBackToListRequests();
        })
        .catch(() => {
          taskErrorNotification();
        });
    },
    [handleBackToListRequests, taskId, comments]
  );

  useEffect(() => {
    if (!formData?.empNumber) {
      return;
    }

    verifyEmployee(formData.empNumber)
      .unwrap()
      .then((data) => {
        formik.setFieldValue('employeeName', data.empName);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [formData?.empNumber]);

  useEffect(() => {
    if (!formData?.filepath || !formData?.requestId) {
      return;
    }

    const documentFolderPath = getDocumentFolder(formData.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, formData.filepath);

        if (matchedDocument) {
          setDocumentId(matchedDocument.documentId);
        }
      });
  }, [formData]);

  useEffect(() => {
    if (!requestedUser || !formData) {
      return;
    }

    formik.setValues({
      employeeNumber: requestedUser.empNumber,
      employeeName: requestedUser.empName,
      designation: requestedUser.occupation,
      currentLocation: requestedUser.currentLocation,
      placeOfTravel: formData.destination,
      dateOfTravel: formData.travelDate,
      returnDate: formData.returnDate,
      totDays: String(formData.totDays),
      purposeOfTravel: formData.remarks,
      approvedTravelAmount: formData.eligibleDays,
      country: String(formData.leaveTypeID),
      sponsoredBy: String(formData.ticketRequired),
      sponsoredByExtra: formData.batchNo,
    });
  }, [formData, requestedUser]);

  const dataSourceOverseasMissionDetails = useMemo(() => {
    const countryLabel = countries.find(
      (country) => country.id === Number(formik.values.country)
    )?.countryName;
    const sponsorLabel = sponsors.find(
      (sponsor) => String(sponsor.codeNum) === formik.values.sponsoredBy
    )?.codeDesc;
    const documentEl = documentId
      ? {
          label: 'Documents',
          value: formData.filepath,
          extra: {
            document: {
              documentId,
            },
          },
        }
      : null;

    const output = [
      { label: 'Employee Number', value: formik.values.employeeNumber || '' },
      { label: 'Employee Name', value: formik.values.employeeName },
      {
        label: 'Designation',
        value: formik.values.designation || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Current Location',
        value: formik.values.currentLocation || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Place of Travel',
        value: formik.values.placeOfTravel,
      },
      {
        label: 'Date of Travel',
        value: parseDateTime(formik.values.dateOfTravel),
      },
      { label: 'Return Date', value: parseDateTime(formik.values.returnDate) },
      {
        label: 'Total No. of days',
        value: formik.values.totDays,
      },
      {
        label: 'Purpose of Travel',
        value: formik.values.purposeOfTravel || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Approved Travel Amount',
        value: formik.values.approvedTravelAmount || EMPTY_FIELD_PLACEHOLDER,
      },
      documentEl,
      {
        label: 'Country',
        value: countryLabel || EMPTY_FIELD_PLACEHOLDER,
      },
      { label: 'Sponsored by', value: sponsorLabel || EMPTY_FIELD_PLACEHOLDER },
    ];

    if (formik.values.sponsoredBy === String(SponsorTypeCodes.COMPANY)) {
      output.push({
        label: 'Company name',
        value: formik.values.sponsoredByExtra,
      });
    }

    if (formik.values.sponsoredBy === String(SponsorTypeCodes.SUPPLIER)) {
      output.push({
        label: 'Supplier Name',
        value: formik.values.sponsoredByExtra,
      });
    }

    if (formik.values.sponsoredBy === String(SponsorTypeCodes.SUB_CONTRACTOR)) {
      output.push({
        label: 'Sub-contractor Name',
        value: formik.values.sponsoredByExtra,
      });
    }

    return output.filter((item) => Boolean(item)) as ApproveListData;
  }, [formik.values, countries, sponsors, formData, documentId]);

  const handleFooterAction = useCallback(
    (action: FooterActions, payload?: string) => {
      if (action === FooterActions.APPROVE || action === FooterActions.REJECT) {
        handleApproveReject(action);
      }

      if (action === FooterActions.COMMENTS_CHANGE) {
        setCommentValue(payload || '');
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }

      if (action === FooterActions.REASSIGN) {
        setReassignOpen(true);
      }
    },
    [handleBackToListRequests, handleApproveReject]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={requestedUser}
        requisitionInfo={requisitionInfo}
        formType="approval"
        onFooterAction={handleFooterAction}
      >
        <FormItems>
          <ApproveList
            dataSource={dataSourceOverseasMissionDetails}
            label="Overseas Mission Details"
          />
        </FormItems>
      </MainForm>
      <ReassignWindow
        creatorRequestNumber={requestedUser?.empNumber}
        taskId={taskId}
        isOpen={isReassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        onUserSelected={() => {
          setReassignOpen(false);
          handleBackToListRequests();
        }}
      />
    </Flex>
  );
};

export default OverseasMissionCreateForm;
